import React, { useMemo, useCallback } from 'react';
import Link from 'next/link';

import { textBlue, text3 } from '@osmosis/tailwind-ui/styles/text';
import { Flex } from '@osmosis/tailwind-ui/components/Box';
import { Icon } from '@osmosis/tailwind-ui/components/Icon';
import { Checkbox } from '@osmosis/tailwind-ui/components/Checkbox';

import { faPlay } from '@fortawesome/pro-solid-svg-icons/faPlay';
import { faCheckCircle } from '@fortawesome/pro-solid-svg-icons/faCheckCircle';
import { faFileAlt } from '@fortawesome/pro-regular-svg-icons/faFileAlt';
import { PageType } from '@osmosis/shared/types/learn';

import ProgressCircle from '../../ProgressCircle';
import { PlaylistProps } from '@osmosis/shared/types/playlist';

import LearnSidenavItemRowName from './LearnSidenavItemRowName';
import { track } from '../../../lib/util/tracking';
import { useUserContext } from '../../../context/UserContext';
import { HoveredItem } from './VideoList';
import { useIntl } from 'react-intl';
import { bg0 } from '@osmosis/tailwind-ui/styles';

const WIDTH = 30;

type LearnSidenavItemRowProps = {
  title: string;
  learnNodeID: number;
  link: string;
  type: PageType;
  prime?: boolean;
  playlists?: PlaylistProps[];
  selectionMode?: boolean;
  current?: boolean;
  checked?: boolean;
  progress?: number; // 0-100
  onChangeCheckedValue?: () => void;
  isOnScreen?: boolean;
  user: ReturnType<typeof useUserContext>['user'];
  hasPrime: boolean;
  tbr: boolean;
  handleItemHover?: (item: HoveredItem) => void;
  addToPlaylistIcon?: React.ReactNode | null;
};

const NoteIcon = () => (
  <Flex
    className={`left-[-3px] border-gray-200 items-center relative z-[100] justify-center p-1 ${bg0} rounded-[100%] border-1 border-solid h-[${WIDTH}px] w-[${WIDTH}px]`}
  >
    <Icon icon={faFileAlt} />
  </Flex>
);

/**
 * LearnSidenavItemRow.
 */
const LearnSidenavItemRow = React.memo(function LearnSidenavItemRow({
  title,
  link,
  type,
  selectionMode = false,
  current = false,
  checked = true,
  progress = 0,
  prime,
  learnNodeID,
  onChangeCheckedValue,
  isOnScreen,
  user,
  hasPrime,
  tbr,
  handleItemHover,
  addToPlaylistIcon = null,
}: LearnSidenavItemRowProps): JSX.Element {
  /**
   * PlayIconWithProgress.
   */
  const PlayIconWithProgress = () =>
    progress <= 80 ? (
      <ProgressCircle
        progress={progress}
        size="24"
        lineWidth="32"
        fillColor="white"
        style={{ zIndex: 1 }}
      >
        <Icon
          icon={faPlay}
          width="12"
          height="12"
          className={`${current ? textBlue : text3}`}
          viewBox="-400 -320 40 40"
        />
      </ProgressCircle>
    ) : (
      <Icon
        icon={faCheckCircle}
        width="24"
        height="24"
        className={`z-[100] ${textBlue}`}
      />
    );

  const icon = type === 'video' ? <PlayIconWithProgress /> : <NoteIcon />;

  const handleClick = async () => {
    await track(
      'concept_card_interaction',
      {
        feature: 'learn-page-sidebar',
        interaction: `play-video`,
      },
      ['keen'],
    );
  };
  const progressValue = useMemo(
    () => `${progress}%: ${title}`,
    [progress, title],
  );

  const handleMouseEnter = useCallback(() => {
    handleItemHover && handleItemHover({ learnNodeID, title, current });
  }, []);

  const intl = useIntl();
  const addToPlaylistLabel = intl.formatMessage({
    defaultMessage: 'Add to playlist',
    id: 'playlist.add_to_playlist_button',
  });

  const canAddToPlaylist = !!user && !tbr;

  return (
    // Link renders an anchor with an href, so Enter already activates it; onClick only tracks
    // eslint-disable-next-line @nx/workspace/a11y/require-keyboard-handlers
    <Link
      href={link}
      className="group flex items-center cursor-pointer"
      aria-label={progressValue}
      data-testid={`learn-sidenav-${title}`}
      onClick={handleClick}
      onMouseEnter={handleMouseEnter}
      onFocus={handleMouseEnter}
    >
      {selectionMode && !tbr ? (
        <Checkbox
          checked={checked}
          onChange={() => {
            onChangeCheckedValue?.();
          }}
          className="m-0"
          data-testid={`learn-sidenav-item-row-${title}-checkbox`}
          style={{ width: '30px' }}
        />
      ) : (
        isOnScreen && icon
      )}
      <div
        className={`flex ml-2 w-full items-center justify-between rounded-[5px] ml-2 w-full flex-1 px-2 py-1 my-1 ${
          current
            ? 'bg-[#d3e3f9] text-blue-600'
            : 'group-hover:bg-gray-100/[0.7] group-focus-within:bg-gray-100/[0.7] group-focus:bg-gray-100/[0.7] group-active:bg-gray-100/[0.7]'
        }`}
      >
        <LearnSidenavItemRowName
          title={title}
          learnNodeID={learnNodeID}
          current={current}
          prime={prime}
          user={user}
          hasPrime={hasPrime}
          isOnScreen={isOnScreen}
        />
        {/* the real button is the child; this slot only stops the click reaching the row link */}
        {/* eslint-disable-next-line @nx/workspace/a11y/forbid-div-with-onclick, @nx/workspace/a11y/require-keyboard-handlers */}
        <span
          role="presentation"
          onClick={
            addToPlaylistIcon
              ? (e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }
              : undefined
          }
          className={
            canAddToPlaylist
              ? 'flex h-7 w-9 shrink-0 items-center justify-end'
              : undefined
          }
          aria-label={addToPlaylistIcon ? addToPlaylistLabel : undefined}
          data-testid={`learn-sidenav-item-row-${title}-add-to-playlist-button`}
        >
          {addToPlaylistIcon}
        </span>
      </div>
    </Link>
  );
});
export default LearnSidenavItemRow;
